import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Dialog, InlineError, Input, Select, Skeleton } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { useWorkspaceSettings } from '../settings/hooks';
import { useCgClientCompanies, useCgClientLeads } from './clientDirectory';
import { cgKeys, useCgProductTypes } from './hooks';
import { useComponentTypes } from '../supplierExchange/sampleReview';
import { attachProof, draftProblems, emptyDraft, isReady, removeProof } from './clientLink';
import { createClientLink, emptyProgress } from './clientLinkService';
import type { ClientLinkDraft, DraftProblem } from './clientLink';
import type { ClientLinkProgress, ProofFailure } from './clientLinkService';

/**
 * + New Client Link.
 *
 * One modal that goes from "we have four mockups" to "here is a URL I can
 * paste into an email", because the alternative is what exists today: create
 * an order in one place, find it again, attach assets one at a time somewhere
 * else, and then have no way at all to mint the link.
 *
 * ── What the dialog is careful about ───────────────────────────────────────
 *
 * The output is a PUBLIC URL. Every guard here exists because the failure it
 * prevents is one a client sees:
 *
 *   * all four proofs before the token — a link to a half-empty review
 *   * the token minted last and once  — a live URL that predates its content
 *   * retry resumes                    — a second press must not write a
 *                                        second order or a second v1
 *   * the link shown, not auto-sent    — the person who owns the relationship
 *                                        decides when the client hears from us
 *
 * Nothing here emails anybody. The dialog produces the link and stops.
 */

function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/**
 * The failure, said in words the person can act on.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * Kyu, 31 Aug, blocked on a real client link: the box said "Failed to fetch".
 * That is the browser's own wording for "the request never reached the other
 * end", and it names neither what was being reached nor what to do. Three of
 * the four strings that can land here are like that — they are identifiers
 * meant for a log, and they were being printed at a person.
 *
 * Anything unrecognised is passed through UNCHANGED rather than replaced with
 * a friendly guess. A wrong-but-warm message is worse than a technical one: it
 * sends somebody looking in the wrong place, and it hides the string that
 * would have told a developer what happened.
 */
function explainFailure(message: string): string {
  if (/failed to fetch|networkerror|load failed/i.test(message)) {
    // The proofs go to Google Drive, and this is what a browser says when that
    // request does not leave the machine: offline, or something between the
    // browser and googleapis.com refusing it — an extension, a VPN, a network
    // that filters Google endpoints.
    return 'the upload to Google Drive could not be reached. Check the connection, and whether a browser extension or VPN is blocking Google.';
  }
  if (message === 'drive_reconnect_required') {
    return 'Google Drive needs reconnecting before proofs can be uploaded.';
  }
  if (message === 'upload_returned_the_wrong_number_of_files') {
    return 'Drive accepted a different number of files than were sent. Try again; if it repeats, the proofs need checking one at a time.';
  }
  if (message === 'token_not_returned') {
    return 'the order was written but no review link came back.';
  }
  const driveStatus = /^drive_error_(\d{3})$/.exec(message);
  if (driveStatus) {
    return `Google Drive refused the upload (${driveStatus[1] ?? ''}).`;
  }
  return message;
}

function problemFor(problems: DraftProblem[], field: DraftProblem['field']): string | undefined {
  return problems.find((problem) => problem.field === field)?.message;
}

export function NewClientLinkDialog({
  open,
  onClose,
  origin = window.location.origin,
}: {
  open: boolean;
  onClose: () => void;
  /** Injected in tests; the browser knows its own origin. */
  origin?: string;
}) {
  const { client, access } = useAuth();
  // 21 is the cg_orders.sample_due_days column default; this falls back to it
  // while the workspace query is in flight.
  const workspaceSampleDays = useWorkspaceSettings().data?.default_sample_due_days ?? 21;
  const queryClient = useQueryClient();
  const companies = useCgClientCompanies(open);
  const productTypes = useCgProductTypes();
  const componentTypes = useComponentTypes();

  const [draft, setDraft] = useState<ClientLinkDraft>(emptyDraft);
  const [progress, setProgress] = useState<ClientLinkProgress>(emptyProgress);
  const [failures, setFailures] = useState<ProofFailure[]>([]);
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showProblems, setShowProblems] = useState(false);
  const [copied, setCopied] = useState(false);
  const [components, setComponents] = useState<string[] | null>(null);

  const leads = useCgClientLeads(draft.companyId);

  // NOTHING IS SUGGESTED HERE ANY MORE, and that is the point.
  //
  // This box used to arrive pre-filled with the next number in the Hub's own
  // sequence, under a hint that read "Suggested from the last numbered order."
  // But cg_orders.order_no IS the Shopify order number — two migrations
  // refused a second column for exactly that reason — so the Hub's sequence is
  // not a source of truth about it, it is a coincidence that holds only while
  // nobody creates an order anywhere else.
  //
  // Ced, 3 September: "di kasi naga match ang order number sa shopify and sa
  // hub… si Lazzara Orthodontics is 1042 sa shopify pero 1039 sa hub." He did
  // not mistype it. The Hub offered him a plausible wrong number and told him
  // it was the right one, and the number it offered is the number that ends up
  // printed on a purchase order to El Salvador as CG-PO-#1039.
  //
  // An empty box asks a question. A filled one answers it, and it was
  // answering wrongly.

  useEffect(() => {
    if (open && components === null && componentTypes.isSuccess && !componentTypes.isFetching) {
      setComponents(componentTypes.data.map((component) => component.id));
    }
  }, [componentTypes.data, componentTypes.isFetching, componentTypes.isSuccess, components, open]);

  const problems = components === null ? [] : draftProblems(draft, components);
  const actorId = access.status === 'approved' ? access.profile.id : '';
  const started = progress.orderId !== null;
  const done = url !== null;

  function reset() {
    setDraft(emptyDraft());
    setProgress(emptyProgress());
    setFailures([]);
    setUrl(null);
    setBusy(false);
    setError('');
    setShowProblems(false);
    setCopied(false);
    setComponents(null);
  }

  function close() {
    reset();
    onClose();
  }

  async function submit() {
    if (components === null || !isReady(draft, components)) {
      setShowProblems(true);
      return;
    }
    const company = (companies.data ?? []).find((entry) => entry.id === draft.companyId);
    setError('');
    setBusy(true);
    try {
      const outcome = await createClientLink(
        { client, actorId, origin, sampleDueDays: workspaceSampleDays },
        draft,
        progress,
        company?.name ?? '',
        components,
      );
      setProgress(outcome.progress);
      setFailures(outcome.failures);
      setUrl(outcome.url);
      if (outcome.failures.length > 0) {
        // Accurate as of Batch 11b: the proofs go up BEFORE anything is
        // written, and the writes are one transaction. So a failure here means
        // nothing was created — which is the reassuring half of the sentence
        // and the half the old wording got wrong.
        //
        // Kyu, 31 Aug, on the real thing: the box read "Failed to fetch
        // Nothing was created — fix it and press the button again." Two
        // sentences run together with no stop between them, the raw text of a
        // browser exception at the front, and no word about WHICH of the four
        // proofs failed. `component` was on the failure the whole time and was
        // being thrown away here. "Fix it" is not an instruction if the screen
        // will not say what "it" is.
        setError(
          `${outcome.failures
            .map((failure) => `${failure.component} — ${explainFailure(failure.message)}`)
            .join(
              ' ',
            )} Nothing was created, so nothing needs undoing. Fix that and press the button again.`,
        );
      }
      void queryClient.invalidateQueries({ queryKey: cgKeys.all });
    } catch (cause) {
      // Only the order write reaches here; nothing has been created yet.
      setError(messageOf(cause));
    }
    setBusy(false);
  }

  const buttonLabel = busy
    ? 'Working…'
    : failures.length > 0
      ? 'Retry and generate link'
      : 'Create link';

  return (
    <Dialog
      open={open}
      wide
      title="New Client Link"
      onClose={busy ? () => undefined : close}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={busy}>
            {done || started ? 'Close' : 'Cancel'}
          </Button>
          {done ? null : (
            <Button
              onClick={() => {
                void submit();
              }}
              disabled={busy}
            >
              {buttonLabel}
            </Button>
          )}
        </>
      }
    >
      {error === '' ? null : <InlineError>{error}</InlineError>}

      {done ? (
        <div className="client-link-result">
          <h3 className="client-link-heading">The link is live</h3>
          <p className="client-link-hint">
            Send it to the contact yourself. Nothing has been emailed — the review opens without a
            login, so the link is the credential.
          </p>
          <div className="client-link-url">
            <Input label="Review link" readOnly value={url} onFocus={(e) => e.target.select()} />
            <Button
              variant="secondary"
              onClick={() => {
                void navigator.clipboard?.writeText(url).then(
                  () => setCopied(true),
                  () => setCopied(false),
                );
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="client-link-hint">
            Expires in {draft.expiryDays} days. All four components are at v1; a revision the client
            asks for reopens only that slot.
          </p>
        </div>
      ) : (
        <>
          <h3 className="client-link-heading">Who it is for</h3>
          {companies.isPending ? (
            <Skeleton lines={2} label="Loading clients" />
          ) : companies.isError ? (
            <InlineError>Clients could not be loaded.</InlineError>
          ) : (
            <>
              <Select
                label="Company"
                placeholder="Choose a company"
                value={draft.companyId}
                error={showProblems ? problemFor(problems, 'company') : undefined}
                disabled={started || busy}
                options={(companies.data ?? []).map((entry) => ({
                  value: entry.id,
                  label: entry.name,
                }))}
                // Changing company clears the contact: a lead from the previous
                // company would still be a valid uuid and would silently record
                // the job against the wrong person.
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    companyId: event.target.value,
                    leadId: '',
                  }))
                }
              />
              <Select
                label="Contact who will review"
                placeholder={draft.companyId === '' ? 'Choose a company first' : 'Choose a contact'}
                value={draft.leadId}
                error={showProblems ? problemFor(problems, 'lead') : undefined}
                disabled={draft.companyId === '' || started || busy}
                options={(leads.data ?? []).map((entry) => ({
                  value: entry.id,
                  label:
                    entry.title === '' ? entry.full_name : `${entry.full_name} — ${entry.title}`,
                }))}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, leadId: event.target.value }))
                }
              />
            </>
          )}

          <h3 className="client-link-heading">The job</h3>
          <Input
            label="Shopify order #"
            value={draft.orderNo}
            placeholder="#1042"
            hint="Copy it from the order in Shopify. This becomes the PO number the factory quotes back, and the number the client sees on their review page — it is not a Hub number and nothing renumbers it later."
            error={showProblems ? problemFor(problems, 'orderNo') : undefined}
            disabled={started || busy}
            onChange={(event) =>
              setDraft((current) => ({ ...current, orderNo: event.target.value }))
            }
          />
          <Select
            label="Product type"
            placeholder="Choose a product type"
            value={draft.productType}
            error={showProblems ? problemFor(problems, 'productType') : undefined}
            disabled={started || busy}
            options={(productTypes.data ?? []).map((entry) => ({
              value: entry.id,
              label: entry.id,
            }))}
            onChange={(event) =>
              setDraft((current) => ({ ...current, productType: event.target.value }))
            }
          />
          <Input
            label="Units"
            inputMode="numeric"
            value={draft.units}
            error={showProblems ? problemFor(problems, 'units') : undefined}
            disabled={started || busy}
            onChange={(event) => setDraft((current) => ({ ...current, units: event.target.value }))}
          />
          <Input
            label="Unit price (USD)"
            inputMode="decimal"
            value={draft.unitPriceUsd}
            error={showProblems ? problemFor(problems, 'price') : undefined}
            disabled={started || busy}
            onChange={(event) =>
              setDraft((current) => ({ ...current, unitPriceUsd: event.target.value }))
            }
          />
          <Input
            label="Link expires in (days)"
            inputMode="numeric"
            value={draft.expiryDays}
            error={showProblems ? problemFor(problems, 'expiry') : undefined}
            disabled={started || busy}
            onChange={(event) =>
              setDraft((current) => ({ ...current, expiryDays: event.target.value }))
            }
          />

          <h3 className="client-link-heading">Design proofs</h3>
          <p className="client-link-hint">
            One per component, all four required. These land as v1; versions only advance when the
            client asks for a revision.
          </p>
          {componentTypes.isError ? (
            <InlineError>Artwork components could not be loaded.</InlineError>
          ) : components === null ? (
            <Skeleton lines={2} label="Loading artwork components" />
          ) : showProblems && problemFor(problems, 'proofs') !== undefined ? (
            <InlineError>{problemFor(problems, 'proofs')}</InlineError>
          ) : null}
          <ul className="client-link-slots">
            {(components ?? []).map((component) => (
              <ProofSlot
                key={component}
                component={component}
                file={draft.proofs[component]}
                sent={progress.uploaded.includes(component)}
                failure={failures.find((entry) => entry.component === component)}
                busy={busy}
                onChoose={(file) => setDraft((current) => attachProof(current, component, file))}
                onRemove={() => setDraft((current) => removeProof(current, component))}
              />
            ))}
          </ul>
        </>
      )}
    </Dialog>
  );
}

function ProofSlot({
  component,
  file,
  sent,
  failure,
  busy,
  onChoose,
  onRemove,
}: {
  component: string;
  file: File | undefined;
  sent: boolean;
  failure: ProofFailure | undefined;
  busy: boolean;
  onChoose: (file: File) => void;
  onRemove: () => void;
}) {
  return (
    <li className="client-link-slot">
      <div className="client-link-slot-head">
        <span className="client-link-slot-name">{component}</span>
        {sent ? (
          <Badge tone="success">Sent as v1</Badge>
        ) : failure !== undefined ? (
          <Badge tone="warning">Upload failed</Badge>
        ) : file === undefined ? (
          <Badge tone="neutral">No proof</Badge>
        ) : (
          <Badge tone="brand">Ready</Badge>
        )}
      </div>
      <p className="client-link-slot-file">{file?.name ?? 'Nothing chosen'}</p>
      {failure === undefined ? null : <p className="client-link-slot-error">{failure.message}</p>}
      {/* Once a proof is written it is a versioned row that this dialog cannot
          delete — order_assets_guard_delete blocks it — so Replace and Remove
          both stop meaning anything. */}
      {sent ? null : (
        <div className="client-link-slot-actions">
          <label className="ui-button ui-button--secondary ui-button--sm">
            {file === undefined ? 'Choose proof' : 'Replace'}
            <input
              type="file"
              accept="image/*,application/pdf"
              aria-label={`Choose a proof for ${component}`}
              disabled={busy}
              style={{ display: 'none' }}
              onChange={(event) => {
                const chosen = event.target.files?.[0];
                event.target.value = '';
                if (chosen === undefined) return;
                onChoose(chosen);
              }}
            />
          </label>
          {file === undefined ? null : (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              aria-label={`Remove the proof for ${component}`}
              onClick={onRemove}
            >
              Remove
            </Button>
          )}
        </div>
      )}
    </li>
  );
}
